import type { GuessPicture } from "./guess/api";
import type { ClockEvent } from "./guess/clock";
import type { Lang } from "./types";

export interface BluffItem {
  id: string;
  q: string; // contains exactly one "____"
  a: string;
  alt: string[];
  lies: string[];
  cat: string;
  diff: 1 | 2 | 3;
  fun?: boolean;
  adult?: boolean;
}

export interface TrufItem {
  id: string;
  s: string;
  t: boolean;
  x: string;
  cat: string;
  diff: 1 | 2 | 3;
  fun?: boolean;
  adult?: boolean;
}

export interface SpyPack {
  id: string;
  name: string;
  adult: boolean;
  emoji: string;
  words: string[];
  questions: string[];
}

export interface SpyData {
  lang: Lang;
  general: string[];
  packs: SpyPack[];
}

export interface GuessCategoryRef {
  slug: string;
  name: string;
  kind: "image" | "flag" | "emoji";
}

/** Guess in online rooms: the secret picture sequence, judging and history live on the server. */
export interface GuessContentAccess {
  createMatch(p: {
    category: string;
    lang: Lang;
    players: { name: string; avatar: number; userId: number | null }[];
    roomCode: string;
  }): { matchId: string; pictures: GuessPicture[]; category: GuessCategoryRef } | null;
  /** A passed voice test (or, with `profile`, the signed-in player's voice profile) → its language. */
  voiceOk(p: { testId?: string; profile?: boolean; userId: number | null }): "ka" | "en" | null;
  /** Save the finished match once (the server produced the log); returns whether it was verified. */
  finish(matchId: string, events: ClockEvent[]): boolean;
  abort(matchId: string): void;
}

/** Synchronous access to already-loaded question banks. */
export interface ContentAccess {
  bluff(lang: Lang): BluffItem[];
  truf(lang: Lang): TrufItem[];
  spy(lang: Lang): SpyData;
  /** Server only (online rooms). */
  guess?: GuessContentAccess;
}

export const BLUFF_CATS = [
  "weird", "animals", "body", "science", "history", "laws", "money",
  "places", "food", "popculture", "sport", "georgia", "night",
] as const;

export const TRUF_CATS = [
  "animals", "body", "science", "space", "history", "geo", "food",
  "popculture", "sport", "laws", "myths", "georgia", "tech", "night",
] as const;

export const ADULT_CATS = new Set(["night"]);

export const BLANK = "____";

export const EMPTY_SPY = (lang: Lang): SpyData => ({ lang, general: [], packs: [] });
